import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import complaintFeedbackApi from '../../api/complaintFeedback';
import moment from 'moment';

// 格式化时间数组为字符串
const formatTimeArray = (timeArray) => {
  if (Array.isArray(timeArray) && timeArray.length >= 6) {
    return moment(new Date(timeArray[0], timeArray[1] - 1, timeArray[2], 
                           timeArray[3], timeArray[4], timeArray[5])).format('YYYY-MM-DD HH:mm:ss');
  }
  return '-';
};

// 解析业务信息
const parseBusinessInfo = (relatedType, businessInfoStr) => {
  if (!businessInfoStr) return null;

  try {
    const businessInfo = JSON.parse(businessInfoStr);
    
    // 根据关联类型返回不同的业务信息
    switch (relatedType) {
      case 1: // 商品
        return {
          type: '商品',
          name: businessInfo.name,
          price: businessInfo.price,
          originalPrice: businessInfo.originalPrice,
          status: {
            'ON_SHELF': '上架',
            'OFF_SHELF': '下架',
            'DRAFT': '草稿'
          }[businessInfo.status] || businessInfo.status,
          auditStatus: {
            'PENDING': '待审核',
            'APPROVED': '已审核',
            'REJECTED': '已拒绝'
          }[businessInfo.auditStatus] || businessInfo.auditStatus,
          stock: businessInfo.stock,
          salesVolume: businessInfo.salesVolume,
          createTime: formatTimeArray([
            new Date(businessInfo.createTime).getFullYear(),
            new Date(businessInfo.createTime).getMonth() + 1,
            new Date(businessInfo.createTime).getDate(),
            new Date(businessInfo.createTime).getHours(),
            new Date(businessInfo.createTime).getMinutes(),
            new Date(businessInfo.createTime).getSeconds()
          ])
        };
      case 2: // 用户
        return {
          type: '用户',
          username: businessInfo.username,
          nickname: businessInfo.nickname,
          status: {
            'NORMAL': '正常',
            'RESTRICTED': '受限',
            'BANNED': '禁用'
          }[businessInfo.userStatus] || businessInfo.userStatus
        };
      case 3: // 动态
        return {
          type: '动态',
          id: businessInfo.id,
          userId: businessInfo.userId,
          content: businessInfo.content,
          status: {
            'AUDITING': '审核中',
            'REJECTED': '审核驳回',
            'APPROVED': '审核通过',
            'DELETED': '已删除',
          }[businessInfo.status] || businessInfo.status
        };
      case 4: // 求购
        return {
          type: '求购',
          id: businessInfo.id,
          userId: businessInfo.userId,
          title: businessInfo.title,
          content: businessInfo.content,
          status: {
            'OFF_SHELF': '已下架',
            'AUDITING': '审核中',
            'APPROVED': '审核通过',
            'REJECTED': '审核驳回',
          }[businessInfo.status] || businessInfo.status
        };
      default:
        return null;
    }
  } catch (error) {
    console.error('解析业务信息失败:', error);
    return null;
  }
};

// 分页查询投诉反馈列表
export const fetchComplaintFeedbackList = createAsyncThunk(
  'complaintFeedback/fetchList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const {
        page = 1, 
        pageSize = 10, 
        sortField = 'create_time', 
        sortOrder = 'desc',
        title,
        content,
        typeId,
        status,
        priority,
        startTime,
        endTime,
        ...otherParams
      } = params;
     
      const queryParams = {
        page,
        pageSize,
        sortField,
        sortOrder
      };

      // 添加搜索参数
      if (title) queryParams.title = title;
      if (content) queryParams.content = content;
      if (typeId) queryParams.typeId = typeId;
      if (status !== undefined) queryParams.status = status;
      if (priority) queryParams.priority = priority;
      if (startTime) queryParams.startTime = startTime;
      if (endTime) queryParams.endTime = endTime;
  
      const response = await complaintFeedbackApi.listComplaintFeedbacks(queryParams);
      
      // 处理响应数据
      const processedData = {
        records: (response.data?.records || []).map(item => ({
          ...item,
          createTime: formatTimeArray(item.createTime),
          updateTime: formatTimeArray(item.updateTime),
          resolveTime: formatTimeArray(item.resolveTime),
          
          // 类型映射
          typeText: {
            1: '产品投诉',
            2: '服务投诉',
            3: '功能建议',
            4: 'Bug反馈',
            5: '其他'
          }[item.typeId] || '未知',

          // 状态映射
          statusText: {
            0: '待处理',
            1: '处理中',
            2: '已处理',
            3: '已关闭'
          }[item.status] || item.status,

          // 优先级映射
          priorityText: {
            1: '低',
            2: '中',
            3: '高',
            4: '紧急'
          }[item.priority] || '未知',

          // 处理图片数组
          imageList: item.images || []
        })),
        total: response.data?.total || 0,
        current: response.data?.current || page,
        size: response.data?.size || pageSize,
        pages: response.data?.pages || 1
      };

      return processedData;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '获取投诉反馈列表失败' });
    }
  }
);

// 回复投诉反馈
export const replyComplaintFeedback = createAsyncThunk(
  'complaintFeedback/reply',
  async (replyData, { rejectWithValue, dispatch }) => {
    try {
      const response = await complaintFeedbackApi.replyComplaintFeedback(replyData);
      
      // 刷新列表
      dispatch(fetchComplaintFeedbackList());
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '回复投诉反馈失败' });
    }
  }
);

// 更新投诉反馈状态
export const updateComplaintFeedbackStatus = createAsyncThunk(
  'complaintFeedback/updateStatus',
  async ({ id, status }, { rejectWithValue, dispatch }) => {
    try {
      const response = await complaintFeedbackApi.updateComplaintFeedbackStatus(id, status);
      
      // 刷新列表
      dispatch(fetchComplaintFeedbackList());
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '更新投诉反馈状态失败' });
    }
  }
);

// 获取投诉反馈详情
export const fetchComplaintFeedbackDetail = createAsyncThunk(
  'complaintFeedback/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await complaintFeedbackApi.getComplaintFeedbackDetail(id);
      
      // 处理详情数据
      const detail = response.data;
      
      // 格式化时间
      return {
        ...detail,
        createTime: formatTimeArray(detail.createTime),
        updateTime: formatTimeArray(detail.updateTime),
        resolveTime: formatTimeArray(detail.resolveTime),
        
        // 类型映射
        typeText: {
          1: '产品投诉',
          2: '服务投诉',
          3: '功能建议',
          4: 'Bug反馈',
          5: '其他'
        }[detail.typeId] || '未知',

        // 状态映射
        statusText: {
          0: '待处理',
          1: '处理中',
          2: '已处理',
          3: '已关闭'
        }[detail.status] || detail.status,

        // 优先级映射
        priorityText: {
          1: '低',
          2: '中',
          3: '高',
          4: '紧急'
        }[detail.priority] || '未知',

        // 处理图片数组
        imageList: detail.images || [],
        
        // 处理回复列表
        replyList: (detail.replies || []).map(reply => ({
          ...reply,
          createTime: formatTimeArray(reply.createTime)
        })),

        // 处理关联业务信息
        relatedTypeText: {
          1: '商品',
          2: '用户',
          3: '动态',
          4: '求购'
        }[detail.relatedType] || '未知',
        businessInfo: parseBusinessInfo(detail.relatedType, detail.businessInfo)
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '获取投诉反馈详情失败' });
    }
  }
);

const complaintFeedbackSlice = createSlice({
  name: 'complaintFeedback',
  initialState: {
    complaintFeedbacks: [],
    total: 0,
    current: 1,
    pageSize: 10,
    loading: false,
    error: null,
    currentComplaintFeedback: null,
    complaintFeedbackDetail: null,
    detailLoading: false,
    detailError: null
  },
  reducers: {
    setCurrentComplaintFeedback: (state, action) => {
      state.currentComplaintFeedback = action.payload;
    }
  },
  extraReducers: (builder) => {
    // 获取投诉反馈列表
    builder.addCase(fetchComplaintFeedbackList.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchComplaintFeedbackList.fulfilled, (state, action) => {
      state.loading = false;
      state.complaintFeedbacks = action.payload.records;
      state.total = action.payload.total;
      state.current = action.payload.current;
      state.pageSize = action.payload.size;
    });
    builder.addCase(fetchComplaintFeedbackList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 获取投诉反馈详情
    builder.addCase(fetchComplaintFeedbackDetail.pending, (state) => {
      state.detailLoading = true;
      state.detailError = null;
    });
    builder.addCase(fetchComplaintFeedbackDetail.fulfilled, (state, action) => {
      state.detailLoading = false;
      state.complaintFeedbackDetail = action.payload;
    });
    builder.addCase(fetchComplaintFeedbackDetail.rejected, (state, action) => {
      state.detailLoading = false;
      state.detailError = action.payload;
    });

    // 回复投诉反馈
    builder.addCase(replyComplaintFeedback.fulfilled, (state) => {
      // 可以在这里添加额外的状态更新逻辑
    });

    // 更新投诉反馈状态
    builder.addCase(updateComplaintFeedbackStatus.fulfilled, (state) => {
      // 可以在这里添加额外的状态更新逻辑
    });
  }
});

export const { setCurrentComplaintFeedback } = complaintFeedbackSlice.actions;
export default complaintFeedbackSlice.reducer;
